import React, { useEffect, useRef, useState } from 'react';
import {
  Instagram,
  Menu,
  Phone,
  Search,
  ShoppingBag,
  X,
  MessageCircle,
  Facebook
} from 'lucide-react';
import {
  Link,
  useLocation,
  useNavigate
} from 'react-router-dom';

import { getCart } from '../data/mockData';

const WHATSAPP_URL =
  'https://wa.me/355693520111?text=P%C3%ABrsh%C3%ABndetje%2C%20kam%20nj%C3%AB%20pyetje%20p%C3%ABr%20Lor95%20Boutique.';

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  const [cartCount, setCartCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const navLinks = [
    { path: '/', label: 'Kryefaqja' },
    { path: '/collections', label: 'Koleksionet' },
    { path: '/about', label: 'Rreth Nesh' },
    { path: '/visit', label: 'Na Vizitoni' },
    { path: '/contact', label: 'Kontakti' }
  ];

  const updateCartCount = () => {
    const cart = getCart();
    setCartCount(
      cart.reduce(
        (total, item) => total + Number(item.quantity || 0),
        0
      )
    );
  };

  useEffect(() => {
    updateCartCount();

    window.addEventListener('cartUpdated', updateCartCount);

    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (event) => {
    event.preventDefault();

    const query = searchQuery.trim();

    navigate(
      query
        ? `/collections?search=${encodeURIComponent(query)}`
        : '/collections'
    );

    setSearchOpen(false);
    setMenuOpen(false);
    setSearchQuery('');
  };

  return (
    <header className="fixed z-50 top-0 left-0 right-0 pointer-events-none">
      <div className="w-full">

        <div className="pointer-events-auto min-h-[72px] sm:min-h-[76px] border-b border-white/20 bg-black/75 backdrop-blur-xl shadow-[0_14px_45px_rgba(0,0,0,0.25)] px-3 sm:px-6 lg:px-10 xl:px-14 flex items-center justify-between">

          <Link
            to="/"
            className="flex items-center gap-2.5 sm:gap-3 flex-shrink-0 min-w-0"
          >
            <img
              src="/lor95.png"
              alt="Lor95 Boutique"
              className="h-10 sm:h-12 w-auto object-contain flex-shrink-0"
            />

            <div className="block min-w-0">
              <p className="font-serif text-white text-[17px] sm:text-xl lg:text-2xl leading-none font-semibold whitespace-nowrap">
                LOR95
              </p>

              <p className="text-[#d0a04a] text-[7px] sm:text-[9px] tracking-[0.22em] sm:tracking-[0.30em] mt-1 whitespace-nowrap">
                BOUTIQUE
              </p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8 xl:gap-11">
            {navLinks.map((link) => {
              const active = location.pathname === link.path;

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative py-7 text-xs xl:text-sm font-semibold tracking-wide uppercase transition-colors ${
                    active
                      ? 'text-white'
                      : 'text-white/85 hover:text-white'
                  }`}
                >
                  {link.label}

                  <span
                    className={`absolute left-0 right-0 bottom-[18px] h-[2px] bg-[#c8953d] transition-transform origin-left ${
                      active
                        ? 'scale-x-100'
                        : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">

            <div className="hidden md:block relative">
              {searchOpen ? (
                <form onSubmit={submitSearch}>
                  <input
                    ref={searchInputRef}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Kërko..."
                    className="w-44 h-10 bg-white/10 text-white placeholder:text-white/50 border border-white/20 rounded-full pl-4 pr-9 text-sm outline-none focus:border-[#c8953d]"
                  />

                  <button
                    type="button"
                    onClick={() => {
                      setSearchOpen(false);
                      setSearchQuery('');
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
                    aria-label="Mbyll kërkimin"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  className="w-10 h-10 text-white/90 hover:text-[#d1a24e] flex items-center justify-center transition"
                  aria-label="Kërko"
                >
                  <Search className="h-5 w-5" />
                </button>
              )}
            </div>

            <a
              href="tel:+355693520111"
              className="hidden sm:flex w-10 h-10 text-white/90 hover:text-[#d1a24e] items-center justify-center transition"
              aria-label="Telefono"
            >
              <Phone className="h-5 w-5" />
            </a>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex w-10 h-10 text-white/90 hover:text-[#25D366] items-center justify-center transition"
              aria-label="WhatsApp"
              title="Na shkruani në WhatsApp"
            >
              <MessageCircle className="h-5 w-5" />
            </a>

            <a
              href="https://www.instagram.com/lor_95_boutique/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex w-10 h-10 text-white/90 hover:text-[#d1a24e] items-center justify-center transition"
              aria-label="Instagram"
            >
              <Instagram className="h-5 w-5" />
            </a>

            <a
              href="https://www.facebook.com/p/Lor-95-Boutique-100086001116286/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex w-10 h-10 text-white/90 hover:text-[#1877F2] items-center justify-center transition"
              aria-label="Facebook"
              title="Na ndiqni në Facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>

            <Link
              to="/cart"
              className="relative w-10 h-10 text-white/90 hover:text-[#d1a24e] flex items-center justify-center transition"
              aria-label="Shporta"
            >
              <ShoppingBag className="h-5 w-5" />

              {cartCount > 0 && (
                <span className="absolute -right-1 -top-1 h-5 min-w-5 px-1 rounded-full bg-[#c8953d] text-white text-[10px] font-bold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="lg:hidden w-10 h-10 text-white flex items-center justify-center"
              aria-label="Menu"
            >
              {menuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="pointer-events-auto lg:hidden border-t border-white/15 bg-black/90 backdrop-blur-xl overflow-hidden shadow-xl p-4">

            <form
              onSubmit={submitSearch}
              className="relative mb-4"
            >
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />

              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Kërko produkte..."
                className="w-full h-11 pl-10 pr-4 rounded-lg border border-white/15 bg-white/10 text-white placeholder:text-white/50 outline-none"
              />
            </form>

            <nav className="space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium ${
                    location.pathname === link.path
                      ? 'bg-[#c8953d] text-white'
                      : 'text-white/85 hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-white/10">
              <a
                href="tel:+355693520111"
                className="flex items-center justify-center gap-2 h-11 rounded-lg bg-white/10 text-white text-sm"
              >
                <Phone className="h-4 w-4" />
                Telefono
              </a>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 h-11 rounded-lg bg-[#25D366]/15 border border-[#25D366]/30 text-white text-sm"
              >
                <MessageCircle className="h-4 w-4 text-[#25D366]" />
                WhatsApp
              </a>

              <a
                href="https://www.instagram.com/lor_95_boutique/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 h-11 rounded-lg bg-white/10 text-white text-sm"
              >
                <Instagram className="h-4 w-4" />
                Instagram
              </a>

              <a
                href="https://www.facebook.com/p/Lor-95-Boutique-100086001116286/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 h-11 rounded-lg bg-[#1877F2]/15 border border-[#1877F2]/30 text-white text-sm"
              >
                <Facebook className="h-4 w-4 text-[#1877F2]" />
                Facebook
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
